// src/components/MorningBriefing.jsx
// Daily Brief Snapshot (compact) — comprehensive dashboard intelligence card
// Full AI Briefing with inline chat (non-compact, used on /daily-brief page)
import React, { useState, useEffect } from 'react';
import { getDynamicBriefing, getTodayAutomationPreview, cancelQueuedAutomation } from '../api';
import { Link, useNavigate } from 'react-router-dom';
import { useAI } from '../context/AIContext';
import {
  Sun, Moon, Sunset, RefreshCw, Phone, CheckCircle2,
  AlertTriangle, TrendingUp, TrendingDown, Clock, ChevronDown, ChevronUp,
  Bot, Send, MessageCircle, Loader2, ArrowRight, Activity, Heart,
  ClipboardList, Zap, Users, Calendar, Target, BarChart3, Sparkles,
  Mail, MessageSquare as MessageSquareIcon, Eye, Award, X as XIcon
} from 'lucide-react';

const RISK_CHIP_COLORS = {
  severe: 'bg-red-100 text-red-800 border-red-200',
  critical: 'bg-red-50 text-red-700 border-red-100',
  high: 'bg-orange-50 text-orange-700 border-orange-100',
  elevated: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  moderate: 'bg-blue-50 text-blue-700 border-blue-100',
  low: 'bg-green-50 text-green-700 border-green-100',
  stable: 'bg-green-100 text-green-800 border-green-200'
};

function getRiskCategory(score) {
  if (score >= 90) return 'severe';
  if (score >= 80) return 'critical';
  if (score >= 65) return 'high';
  if (score >= 50) return 'elevated';
  if (score >= 35) return 'moderate';
  if (score >= 20) return 'low';
  return 'stable';
}

export default function MorningBriefing({ compact = false }) {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [briefData, setBriefData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const { sendMessage, toggleOpen, isStreaming, loading: aiLoading } = useAI();

  useEffect(() => {
    if (compact) {
      loadComprehensiveBrief();
    } else {
      loadBriefing();
    }
  }, [compact]);

  // Full /morning-brief endpoint for comprehensive snapshot
  async function loadComprehensiveBrief() {
    try {
      setLoading(true);
      setError(null);
      const BASE = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${BASE}/morning-brief`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) {
        setBriefData(json.data);
      } else {
        setError(json.error || 'Failed to load brief');
      }
    } catch (err) {
      console.error('[MorningBriefing] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Dynamic briefing for full mode (used on the old inline display)
  async function loadBriefing() {
    try {
      setLoading(true);
      setError(null);
      const data = await getDynamicBriefing();
      setBriefing(data.briefing);
    } catch (err) {
      console.error('[MorningBriefing] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    if (compact) {
      await loadComprehensiveBrief();
    } else {
      await loadBriefing();
    }
    setRefreshing(false);
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-500">
          <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>Unable to load your daily brief</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── COMPACT MODE: Comprehensive Daily Brief Snapshot ───
  if (compact && briefData) {
    return <CompactSnapshot data={briefData} refreshing={refreshing} onRefresh={handleRefresh} navigate={navigate} />;
  }

  // ─── FULL MODE: AI Assistant Briefing (used inline, not on dashboard) ───
  if (!briefing) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-500">
          <Bot className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>No briefing available</p>
        </div>
      </div>
    );
  }

  // Get time-appropriate icon
  function getTimeIcon() {
    if (!briefing) return <Sun className="w-6 h-6" />;
    switch (briefing.timeOfDay) {
      case 'morning': return <Sun className="w-6 h-6" />;
      case 'afternoon': return <Sunset className="w-6 h-6" />;
      case 'evening': return <Moon className="w-6 h-6" />;
      default: return <Sun className="w-6 h-6" />;
    }
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with assistant greeting */}
      <div
        className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-[#172A3A] to-[#20344A] text-white cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-shrink-0 [&>svg]:w-5 [&>svg]:h-5">
              {getTimeIcon()}
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-sm sm:text-base truncate">
                {briefing.greeting}, {briefing.userName}!
              </h2>
              <p className="text-xs opacity-90 truncate">
                <span className="hidden sm:inline">Your {briefing.timeOfDay} brief from </span>
                <span className="font-semibold">{briefing.assistantName}</span>
                <span className="sm:hidden"> - AI</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
              className="p-1 sm:p-1.5 hover:bg-white/20 rounded-full transition"
              title="Refresh briefing"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Main narrative from the assistant */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200 p-3 sm:p-4">
            <div className="flex items-start gap-2">
              <div className="rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10">
                <Bot className="text-white w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  {briefing.narrative}
                </p>

                {/* Inline chat input */}
                <div className="border-t border-slate-200 mt-3 pt-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (chatInput.trim() && !aiLoading && !isStreaming) {
                        sendMessage(chatInput);
                        setChatInput('');
                        toggleOpen();
                      }
                    }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={`Ask ${briefing.assistantName} anything...`}
                      className="flex-1 px-2.5 py-2 rounded-lg border border-slate-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white text-sm"
                      disabled={aiLoading || isStreaming}
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || aiLoading || isStreaming}
                      className="bg-[#172A3A] text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 px-3 py-2"
                    >
                      {(aiLoading || isStreaming) ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span className="hidden sm:inline text-sm">Ask</span>
                        </>
                      )}
                    </button>
                  </form>
                  <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />
                    Your message will open the full chat
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            <QuickStat
              icon={<AlertTriangle className="w-5 h-5" />}
              label="At Risk"
              value={briefing.stats.criticalRisk + briefing.stats.highRisk}
              color={briefing.stats.criticalRisk > 0 ? 'text-red-600' : 'text-orange-600'}
              bgColor={briefing.stats.criticalRisk > 0 ? 'bg-red-50' : 'bg-orange-50'}
              onClick={() => navigate('/clients?filter=at_risk')}
            />
            <QuickStat
              icon={<Clock className="w-5 h-5" />}
              label="Tasks"
              value={briefing.tasks.pending}
              subtext={briefing.tasks.completed > 0 ? `${briefing.tasks.completed} done` : null}
              color="text-blue-600"
              bgColor="bg-blue-50"
              onClick={() => navigate('/tasks')}
            />
            <QuickStat
              icon={<Phone className="w-5 h-5" />}
              label="Calls"
              value={briefing.todayProgress.calls}
              color="text-green-600"
              bgColor="bg-green-50"
              onClick={() => navigate('/conversations')}
            />
            <QuickStat
              icon={<CheckCircle2 className="w-5 h-5" />}
              label="Healthy"
              value={briefing.stats.healthyClients}
              color="text-emerald-600"
              bgColor="bg-emerald-50"
              onClick={() => navigate('/clients?risk_category=stable')}
            />
          </div>

          {/* Action Items */}
          {briefing.actionItems?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-blue-500" />
                Action Items
              </h3>
              <div className="space-y-2">
                {briefing.actionItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Priority Clients */}
          {briefing.priorityClients?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertTriangle size={16} className="text-orange-500" />
                Priority Clients
              </h3>
              <div className="space-y-2">
                {briefing.priorityClients.slice(0, 5).map((client) => (
                  <Link
                    key={client.id}
                    to={`/clients/${client.id}`}
                    className={`block p-3 rounded-lg border transition-colors hover:shadow-sm ${
                      RISK_CHIP_COLORS[getRiskCategory(client.risk_score)]
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{client.first_name} {client.last_name}</p>
                        <p className="text-sm opacity-75">
                          {client.top_risk_factor || 'High risk score'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold">{client.risk_score}</span>
                        <p className="text-xs opacity-75">Risk Score</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Risk Changes */}
          {briefing.riskChanges?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-red-500" />
                Significant Risk Increases (+10 pts)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {briefing.riskChanges.map((change) => (
                  <Link
                    key={change.id}
                    to={`/clients/${change.id}`}
                    className="p-3 rounded-lg border border-red-200 bg-red-50 hover:shadow-sm transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-red-900">{change.first_name} {change.last_name}</p>
                        <p className="text-xs text-red-700">
                          {change.previous_score} → {change.risk_score}
                        </p>
                      </div>
                      <div className="text-red-600 font-bold">
                        +{change.score_change}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Insights */}
          {briefing.insights?.length > 0 && (
            <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 border-t">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                Insights
              </h3>
              <ul className="space-y-2">
                {briefing.insights.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-blue-500 mt-0.5">•</span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPACT SNAPSHOT — Comprehensive Daily Brief for Dashboard
// ═══════════════════════════════════════════════════════════════

function CompactSnapshot({ data, refreshing, onRefresh, navigate }) {
  const [expanded, setExpanded] = useState(true);
  const [todayAutomations, setTodayAutomations] = useState([]);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const { greeting, scoreChanges, healthEvents, overnightItems, yesterdayActivity, riskDistribution } = data || {};

  // Fetch today's automations
  useEffect(() => {
    (async () => {
      setAutomationsLoading(true);
      try {
        const result = await getTodayAutomationPreview();
        if (result.ok) setTodayAutomations(result.automations || []);
      } catch (e) {
        console.error('Error fetching today automations:', e);
      } finally {
        setAutomationsLoading(false);
      }
    })();
  }, []);

  async function handleCancelAutomation(automationId) {
    if (!window.confirm('Cancel this scheduled message?')) return;
    setCancellingId(automationId);
    try {
      const result = await cancelQueuedAutomation(automationId, 'Cancelled from Daily Brief snapshot');
      if (result.ok) setTodayAutomations(prev => prev.filter(a => a.id !== automationId));
    } catch (e) {
      alert('Failed to cancel: ' + e.message);
    } finally {
      setCancellingId(null);
    }
  }

  // Count urgent items
  const criticalCrossings = scoreChanges?.thresholdCrossings?.critical?.length || 0;
  const highCrossings = scoreChanges?.thresholdCrossings?.high?.length || 0;
  const elevatedCrossings = scoreChanges?.thresholdCrossings?.elevated?.length || 0;
  const emergencyTasks = overnightItems?.tasks?.emergency?.length || 0;
  const urgentTasks = overnightItems?.tasks?.urgent?.length || 0;
  const highTasks = overnightItems?.tasks?.high?.length || 0;
  const totalOvernightTasks = (overnightItems?.totals?.tasks || 0);
  const erVisits = healthEvents?.erVisits?.length || 0;
  const medicationChanges = healthEvents?.medications?.length || 0;
  const adherenceGaps = healthEvents?.adherenceGaps?.length || 0;
  const costEvents = healthEvents?.costEvents?.length || 0;
  const totalHealthEvents = healthEvents?.totals?.total || 0;
  const scoresUp = scoreChanges?.summary?.scoresUp || 0;
  const scoresDown = scoreChanges?.summary?.scoresDown || 0;
  const totalScored = scoreChanges?.summary?.totalScored || 0;
  const noContactCount = scoreChanges?.noContactIncreases?.length || 0;
  const callsMade = yesterdayActivity?.calls?.outbound || 0;
  const callsAnswered = yesterdayActivity?.calls?.answered || 0;
  const smsSent = yesterdayActivity?.sms?.sent || 0;
  const smsDelivered = yesterdayActivity?.sms?.delivered || 0;
  const tasksDone = yesterdayActivity?.tasks?.completed || 0;
  const taskRate = yesterdayActivity?.tasks?.rate || 0;
  const clientsContacted = yesterdayActivity?.clientsContacted || 0;
  const totalClients = riskDistribution?.total || 0;
  const severeClients = riskDistribution?.severe?.count || 0;
  const criticalClients = riskDistribution?.critical?.count || 0;
  const highClients = riskDistribution?.high?.count || 0;
  const elevatedClients = riskDistribution?.elevated?.count || 0;

  // Automation type summary
  const autoTypeSummary = todayAutomations.reduce((acc, a) => {
    acc[a.automation_name] = (acc[a.automation_name] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div
        className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-[#172A3A] to-[#20344A] text-white cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Activity className="w-5 h-5 text-[#FFB800] flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="font-semibold text-sm sm:text-base truncate">Daily Brief</h2>
              <p className="text-xs text-blue-200 truncate">{greeting?.date || new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Link
              to="/daily-brief"
              onClick={(e) => e.stopPropagation()}
              className="hidden sm:flex items-center gap-1 text-xs text-white/80 hover:text-white transition px-2 py-1 rounded hover:bg-white/10"
            >
              Full Brief <ArrowRight className="w-3 h-3" />
            </Link>
            <button
              onClick={(e) => { e.stopPropagation(); onRefresh(); }}
              className="p-1 sm:p-1.5 hover:bg-white/20 rounded-full transition"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-3 space-y-3">

          {/* AI Summary */}
          {greeting?.aiSummary && (
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg border border-slate-200 p-2.5">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700 leading-relaxed">{greeting.aiSummary}</p>
              </div>
            </div>
          )}

          {/* Urgent Alerts Banner */}
          {(criticalCrossings > 0 || erVisits > 0 || emergencyTasks > 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <span className="text-xs font-bold text-red-800 uppercase tracking-wider">Needs Immediate Attention</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {criticalCrossings > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                    {criticalCrossings} crossed critical threshold
                  </span>
                )}
                {erVisits > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                    <Heart className="w-3 h-3" /> {erVisits} ER/hospital event{erVisits !== 1 ? 's' : ''}
                  </span>
                )}
                {emergencyTasks > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                    {emergencyTasks} emergency task{emergencyTasks !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Today's Automations */}
          <div className={`rounded-lg border p-2.5 ${todayAutomations.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Zap className={`w-4 h-4 ${todayAutomations.length > 0 ? 'text-amber-500' : 'text-gray-400'}`} />
                <span className="text-xs font-semibold text-gray-700">Automations Going Out at 1 PM</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${todayAutomations.length > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>
                  {automationsLoading ? '...' : todayAutomations.length}
                </span>
              </div>
              <Link to="/daily-brief" className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
                Manage →
              </Link>
            </div>
            {!automationsLoading && todayAutomations.length > 0 ? (
              <>
                {/* Type breakdown chips */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.entries(autoTypeSummary).map(([name, count]) => (
                    <span key={name} className="px-1.5 py-0.5 bg-white rounded text-[10px] text-gray-700 border border-amber-100">
                      {name}: <strong>{count}</strong>
                    </span>
                  ))}
                </div>
                {/* Top 3 automations with cancel */}
                <div className="space-y-1">
                  {todayAutomations.slice(0, 3).map((auto) => (
                    <div key={auto.id} className="flex items-center justify-between px-2 py-1.5 bg-white rounded-lg border border-amber-100 text-xs">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className={`p-1 rounded ${auto.channel === 'sms' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                          {auto.channel === 'sms' ? <MessageSquareIcon size={12} /> : <Mail size={12} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-gray-900 truncate block">{auto.client.name}</span>
                          <span className="text-[10px] text-gray-500">{auto.automation_name} • {auto.channel.toUpperCase()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <div className="relative group">
                          <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                            <Eye size={12} />
                          </button>
                          <div className="absolute right-0 bottom-full mb-1 w-52 p-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {auto.preview}
                          </div>
                        </div>
                        <button
                          onClick={() => handleCancelAutomation(auto.id)}
                          disabled={cancellingId === auto.id}
                          className="px-1.5 py-0.5 text-[10px] text-red-600 hover:bg-red-50 rounded border border-red-200 disabled:opacity-50"
                        >
                          {cancellingId === auto.id ? '...' : 'Cancel'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {todayAutomations.length > 3 && (
                    <Link to="/daily-brief" className="block text-center text-[10px] text-amber-700 hover:text-amber-800 font-medium py-1">
                      +{todayAutomations.length - 3} more — view in full brief
                    </Link>
                  )}
                </div>
              </>
            ) : !automationsLoading ? (
              <p className="text-[10px] text-gray-500">No automations scheduled today</p>
            ) : null}
          </div>

          {/* Overnight Risk Scoring */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5">
            <SnapshotStat
              icon={<Users className="w-4 h-4" />}
              label="Scored"
              value={totalScored}
              color="text-blue-600"
              bgColor="bg-blue-50"
            />
            <SnapshotStat
              icon={<TrendingUp className="w-4 h-4" />}
              label="Risk Up"
              value={scoresUp}
              subtext={scoreChanges?.summary?.avgIncrease ? `avg +${scoreChanges.summary.avgIncrease}` : null}
              color="text-red-600"
              bgColor="bg-red-50"
              onClick={() => navigate('/daily-brief')}
            />
            <SnapshotStat
              icon={<TrendingDown className="w-4 h-4" />}
              label="Risk Down"
              value={scoresDown}
              subtext={scoreChanges?.summary?.avgDecrease ? `avg -${scoreChanges.summary.avgDecrease}` : null}
              color="text-green-600"
              bgColor="bg-green-50"
            />
            <SnapshotStat
              icon={<Target className="w-4 h-4" />}
              label="No Change"
              value={scoreChanges?.summary?.noChange || 0}
              color="text-gray-600"
              bgColor="bg-gray-50"
            />
          </div>

          {/* Threshold Crossings */}
          {(criticalCrossings > 0 || highCrossings > 0 || elevatedCrossings > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Threshold Crossings:</span>
              {criticalCrossings > 0 && (
                <Link to="/daily-brief" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 hover:shadow-sm transition">
                  <AlertTriangle className="w-3 h-3" /> {criticalCrossings} critical (80+) — Call today
                </Link>
              )}
              {highCrossings > 0 && (
                <Link to="/daily-brief" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200 hover:shadow-sm transition">
                  {highCrossings} high (65-79) — Call within 48h
                </Link>
              )}
              {elevatedCrossings > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                  {elevatedCrossings} elevated (50-64)
                </span>
              )}
            </div>
          )}

          {/* No-Contact Drift */}
          {noContactCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-orange-700 bg-orange-50 rounded-lg px-2.5 py-1.5 border border-orange-100">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                <span className="font-semibold">{noContactCount} client{noContactCount !== 1 ? 's' : ''}</span> drifting — no contact score increasing daily (+0.25/day)
              </span>
            </div>
          )}

          {/* Health Events, Tasks, Book — compact row */}
          <div className="grid grid-cols-3 gap-1.5">
            <div
              className={`rounded-lg p-2 border cursor-pointer hover:shadow-sm transition ${totalHealthEvents > 0 ? 'bg-purple-50 border-purple-100' : 'bg-gray-50 border-gray-100'}`}
              onClick={() => navigate('/daily-brief')}
            >
              <div className="flex items-center gap-1 mb-0.5">
                <Heart className={`w-3.5 h-3.5 ${totalHealthEvents > 0 ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider">Health Events</span>
              </div>
              <div className="text-lg font-bold text-gray-800">{totalHealthEvents}</div>
              <p className="text-[10px] text-gray-500 truncate">
                {[erVisits > 0 && `${erVisits} ER`, medicationChanges > 0 && `${medicationChanges} meds`, adherenceGaps > 0 && `${adherenceGaps} gaps`, costEvents > 0 && `${costEvents} cost`].filter(Boolean).join(', ') || 'None detected'}
              </p>
            </div>
            <div
              className={`rounded-lg p-2 border cursor-pointer hover:shadow-sm transition ${totalOvernightTasks > 0 ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}
              onClick={() => navigate('/daily-brief')}
            >
              <div className="flex items-center gap-1 mb-0.5">
                <ClipboardList className={`w-3.5 h-3.5 ${totalOvernightTasks > 0 ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider">Overnight Tasks</span>
              </div>
              <div className="text-lg font-bold text-gray-800">{totalOvernightTasks}</div>
              <p className="text-[10px] text-gray-500 truncate">
                {[emergencyTasks > 0 && `${emergencyTasks} emergency`, urgentTasks > 0 && `${urgentTasks} urgent`, highTasks > 0 && `${highTasks} high`].filter(Boolean).join(', ') || 'No new tasks'}
              </p>
            </div>
            <div className="rounded-lg p-2 bg-gray-50 border border-gray-100 cursor-pointer hover:shadow-sm transition" onClick={() => navigate('/risk')}>
              <div className="flex items-center gap-1 mb-0.5">
                <BarChart3 className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider">Book Health</span>
              </div>
              <div className="text-lg font-bold text-gray-800">{totalClients}</div>
              <p className="text-[10px] text-gray-500 truncate">
                {[severeClients > 0 && `${severeClients} severe`, criticalClients > 0 && `${criticalClients} crit`, highClients > 0 && `${highClients} high`, elevatedClients > 0 && `${elevatedClients} elev`].filter(Boolean).join(', ') || 'All healthy'}
              </p>
            </div>
          </div>

          {/* Risk Distribution Bar */}
          {riskDistribution && totalClients > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Risk Distribution</span>
              </div>
              <div className="flex rounded-full overflow-hidden h-2.5">
                {[
                  { key: 'severe', color: 'bg-red-800' },
                  { key: 'critical', color: 'bg-red-500' },
                  { key: 'high', color: 'bg-orange-500' },
                  { key: 'elevated', color: 'bg-yellow-500' },
                  { key: 'moderate', color: 'bg-blue-400' },
                  { key: 'low', color: 'bg-green-400' },
                  { key: 'stable', color: 'bg-green-600' },
                ].map(tier => {
                  const count = riskDistribution[tier.key]?.count || 0;
                  if (count === 0) return null;
                  const pct = (count / totalClients * 100);
                  return <div key={tier.key} className={`${tier.color}`} style={{ width: `${pct}%` }} title={`${tier.key}: ${count} (${pct.toFixed(1)}%)`} />;
                })}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] text-gray-400">Severe/Critical</span>
                <span className="text-[9px] text-gray-400">Stable/Low</span>
              </div>
            </div>
          )}

          {/* Yesterday's Activity */}
          {yesterdayActivity && (callsMade > 0 || smsSent > 0 || tasksDone > 0 || clientsContacted > 0) && (
            <div className="bg-gray-50 rounded-lg border border-gray-100 p-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Yesterday's Activity</span>
                {yesterdayActivity?.date && (
                  <span className="text-[10px] text-gray-400">
                    — {new Date(yesterdayActivity.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <div className="text-sm font-bold text-[#172A3A]">{callsMade}</div>
                  <div className="text-[10px] text-gray-500">Calls</div>
                  {callsAnswered > 0 && <div className="text-[9px] text-gray-400">{callsAnswered} answered</div>}
                </div>
                <div className="text-center">
                  <div className="text-sm font-bold text-[#172A3A]">{smsSent}</div>
                  <div className="text-[10px] text-gray-500">SMS</div>
                  {smsDelivered > 0 && <div className="text-[9px] text-gray-400">{smsDelivered} delivered</div>}
                </div>
                <div className="text-center">
                  <div className="text-sm font-bold text-[#172A3A]">{tasksDone}</div>
                  <div className="text-[10px] text-gray-500">Tasks Done</div>
                  {taskRate > 0 && <div className="text-[9px] text-gray-400">{taskRate}% rate</div>}
                </div>
                <div className="text-center">
                  <div className="text-sm font-bold text-[#172A3A]">{clientsContacted}</div>
                  <div className="text-[10px] text-gray-500">Contacted</div>
                </div>
              </div>
              {yesterdayActivity?.wins?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-1 mb-1">
                    <Award className="w-3 h-3 text-green-600" />
                    <span className="text-[10px] font-semibold text-green-700">
                      {yesterdayActivity.wins.length} Win{yesterdayActivity.wins.length !== 1 ? 's' : ''} Yesterday
                    </span>
                  </div>
                  {yesterdayActivity.wins.slice(0, 2).map((win, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-[10px] text-gray-600 ml-4">
                      <CheckCircle2 className="w-2.5 h-2.5 text-green-500 flex-shrink-0" />
                      <span className="font-medium">{win.name}</span> — {win.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Biggest Movers — top 3 increases */}
          {scoreChanges?.biggestMovers?.increases?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-red-500" /> Biggest Risk Increases
                </span>
                <Link to="/daily-brief" className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
                  View all →
                </Link>
              </div>
              <div className="space-y-1">
                {scoreChanges.biggestMovers.increases.slice(0, 3).map((client, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer hover:shadow-sm transition ${
                      client.change >= 20 ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-100'
                    }`}
                    onClick={() => navigate(`/risk?clientId=${client.clientId}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{client.name}</span>
                      <span className="text-gray-500 ml-1.5">{client.primaryReason}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <span className="text-gray-400 text-[11px]">{client.previousScore}→{client.currentScore}</span>
                      <span className={`font-bold ${client.change >= 20 ? 'text-red-700' : 'text-orange-700'}`}>+{client.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Biggest Movers — top 3 decreases (wins) */}
          {scoreChanges?.biggestMovers?.decreases?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <TrendingDown className="w-3 h-3 text-green-500" /> Score Decreases (Wins!)
                </span>
              </div>
              <div className="space-y-1">
                {scoreChanges.biggestMovers.decreases.slice(0, 3).map((client, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded-lg border bg-green-50 border-green-100 text-xs cursor-pointer hover:shadow-sm transition"
                    onClick={() => navigate(`/risk?clientId=${client.clientId}`)}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{client.name}</span>
                      <span className="text-gray-500 ml-1.5">{client.primaryReason}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <span className="text-gray-400 text-[11px]">{client.previousScore}→{client.currentScore}</span>
                      <span className="font-bold text-green-700">{client.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View Full Brief CTA (mobile) */}
          <Link
            to="/daily-brief"
            className="sm:hidden flex items-center justify-center gap-1.5 text-xs font-medium text-[#172A3A] bg-gray-100 hover:bg-gray-200 rounded-lg py-2 transition"
          >
            View Full Daily Brief <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}


function SnapshotStat({ icon, label, value, subtext, color, bgColor, onClick }) {
  return (
    <div
      className={`rounded-lg ${bgColor} border border-gray-100 p-2 ${onClick ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <span className={color}>{icon}</span>
        <span className="font-medium text-gray-500 uppercase tracking-wider text-[9px]">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`font-bold ${color} text-lg`}>{value}</span>
        {subtext && <span className="text-[10px] text-gray-500">{subtext}</span>}
      </div>
    </div>
  );
}

function QuickStat({ icon, label, value, subtext, color, bgColor, onClick }) {
  return (
    <div
      className={`rounded-lg ${bgColor} border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all' : ''} p-3 sm:p-4`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
        <span className={color}>{icon}</span>
        <span className="font-medium text-gray-500 uppercase tracking-wider text-[10px] sm:text-xs">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`font-bold ${color} text-xl sm:text-2xl`}>{value}</span>
        {subtext && <span className="text-xs text-gray-500">{subtext}</span>}
      </div>
    </div>
  );
}
